import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import log from "logbeacon";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { QUERY_KEYS } from "@config/constant";
import { I18nUtil } from "@/src/utils/i18n";
import {
  fetchAllNewsRankingThemeRows,
  type ThemeRowWithRanking,
} from "@/src/utils/newsRanking";

export interface UseNewsRankingThemeRowsOptions {
  locale?: string;
  enabled?: boolean;
  /** 初始数据，通常来自服务端渲染 */
  initialData?: ThemeRowWithRanking[];
  /** 与 initialData 配合：仅在 true 时将 initialData 注入 React Query */
  useServerData?: boolean;
}

/**
 * 客户端获取新闻 ranking themes 列表。
 * 数据拉取与 SSR `newsRanking` 共用 `fetchAllNewsRankingThemeRows`（首包读 total、仅请求有效 offset，失败重试）。
 * 使用 TanStack Query useQuery，缓存策略对齐 useNewsDetail。
 */
export function useNewsRankingThemeRows(
  options: UseNewsRankingThemeRowsOptions = {},
) {
  const {
    locale = "en",
    enabled = true,
    initialData,
    useServerData = false,
  } = options;

  const { openDataApi } = useOspApiClientProvider();

  const queryKey = useMemo(
    () => [QUERY_KEYS.NEWS_RANKING_THEME_ROWS, locale],
    [locale],
  );

  const { data, isLoading, isFetching, isPending, error, refetch } = useQuery<
    ThemeRowWithRanking[],
    Error,
    ThemeRowWithRanking[]
  >({
    queryKey,
    enabled: enabled && !!openDataApi,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    ...(initialData &&
      useServerData && {
        initialData,
      }),
    queryFn: async () => {
      if (!openDataApi) {
        throw new Error("openDataApi is not available");
      }

      log.debug(
        `[useNewsRankingThemeRows] 请求 ranking themes, locale: ${locale}`,
      );

      const language = I18nUtil.getBackgroundLanguageKey(locale);
      const rows = await fetchAllNewsRankingThemeRows(
        {
          getRankingThemes: (params) =>
            openDataApi.themes.getRankingThemes(params),
        },
        language,
      );

      log.debug(`[useNewsRankingThemeRows] 成功，条数: ${rows.length}`);

      return rows;
    },
  });

  return {
    data: data ?? [],
    isLoading,
    isFetching,
    isPending,
    error,
    refetch,
  };
}
